import { useState } from 'react';
import styled from 'styled-components';

import { queryOptions, useQuery } from '@tanstack/react-query';

import Button from 'components/shared/Button';
import StandardModal from 'components/shared/StandardModal';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import useMutateWithToast from 'hooks/useMutateWithToast';
import { StediPayer } from 'models/StediPayer';
import {
  createGroupPayerNetworkStatuses,
  groupPayerNetworkStatusQueryKeys,
  indexGroupPayerNetworkStatuses,
  indexPayerNetworkSummaries,
} from 'services/api/groupPayerNetworkStatuses';
import { validationErrorMessage } from 'services/api/validationErrors';
import { useToastActions } from 'stores/toastStore';
import { BodySmall } from 'styles/typography';

import { countOf } from './facilityNetworkStatusColumns';
import FacilityPicker from './FacilityPicker';
import StatusFields, { NOT_SET, Statuses } from './StatusFields';
import StediPayerTypeahead from './StediPayerTypeahead';
import { useFacilityRows } from './useFacilityRows';

type Named = { id: string; name: string };

// Landing: payers at chosen facilities. Facility page: payers at that facility. Payer page: facilities for that payer.
export type AddVariant =
  | { kind: 'payers' }
  | { kind: 'groupPayers'; group: Named }
  | { kind: 'payerGroups'; stediPayer: Named };

type Step = 'pick' | 'statuses';

// "the selected payer", "the 3 selected payers"
const selectedOf = (count: number, one: string, many: string) =>
  count === 1 ? `the selected ${one}` : `the ${count} selected ${many}`;
// "the payer", "all 3 payers"; with none chosen yet, "the selected payers"
const allOf = (count: number, one: string, many: string) =>
  count === 0 ? `the selected ${many}` : count === 1 ? `the ${one}` : `all ${count} ${many}`;

export default function AddPayerNetworksModal({ variant, onClose }: { variant: AddVariant; onClose: () => void }) {
  // The payer page's flow is one screen; the other two start by picking payers.
  const [step, setStep] = useState<Step>(variant.kind === 'payerGroups' ? 'statuses' : 'pick');
  // Every selection lives here, not in the steps, so Go Back keeps them.
  const [pickedPayers, setPickedPayers] = useState<StediPayer[]>([]);
  const [pickedGroupIds, setPickedGroupIds] = useState(new Set<string>());
  const [statuses, setStatuses] = useState<Statuses>(NOT_SET);

  const facilityRows = useFacilityRows({
    stediPayerId: variant.kind === 'payerGroups' ? variant.stediPayer.id : undefined,
    enabled: variant.kind !== 'groupPayers',
  });
  const offeredFacilities =
    variant.kind === 'payerGroups'
      ? facilityRows.facilities.filter((facility) => !facility.added)
      : facilityRows.facilities;
  // A facility that turned out to be added leaves the list, so it leaves the request too.
  const groupIds = offeredFacilities.filter((facility) => pickedGroupIds.has(facility.id)).map(({ id }) => id);

  // The search offers only payers not yet added, so a pick can only turn out added through a race
  // with another add. Asked after a refusal, and the payer leaves the picks so the add can be retried.
  const [refused, setRefused] = useState(false);
  const pickedAdded = useQuery({
    ...addedLookup(
      variant,
      pickedPayers.map(({ id }) => id)
    ),
    enabled: refused && pickedPayers.length > 0,
  });
  const offeredPayers = pickedPayers.filter(({ id }) => !pickedAdded.data?.includes(id));

  const stediPayerIds = variant.kind === 'payerGroups' ? [variant.stediPayer.id] : offeredPayers.map(({ id }) => id);
  const sentGroupIds = variant.kind === 'groupPayers' ? [variant.group.id] : groupIds;

  const invalidateQueries = useInvalidateQueries();
  const { addToast } = useToastActions();
  const { mutate, isPending } = useMutateWithToast({
    mutationFn: createGroupPayerNetworkStatuses,
    errorToast: validationErrorMessage,
    onSuccess: () => {
      invalidateQueries({ groupPayerNetworkStatuses: true });
      addToast({ text: successText(variant, stediPayerIds.length, sentGroupIds.length) });
      onClose();
    },
    // A refusal is most likely a race with another add; refreshing marks what's now added.
    onError: () => {
      setRefused(true);
      invalidateQueries({ groupPayerNetworkStatuses: true });
    },
  });

  const payerCount = offeredPayers.length;
  const facilityPicker = (
    <FacilityPicker
      facilities={facilityRows.loaded ? offeredFacilities : []}
      selected={pickedGroupIds}
      onChange={setPickedGroupIds}
      emptyText={
        facilityRows.failed
          ? "Facilities couldn't be loaded. Try again."
          : !facilityRows.loaded
            ? 'Loading facilities…'
            : variant.kind === 'payerGroups'
              ? 'Every facility is already added.'
              : 'There are no facilities to add payers to.'
      }
    />
  );

  return (
    <StandardModal title={variant.kind === 'payerGroups' ? 'Add Facilities' : 'Add Payers'} onCancel={onClose}>
      <Body>
        {variant.kind === 'groupPayers' && (
          <StandardModal.RecordName>{`Facility: ${variant.group.name}`}</StandardModal.RecordName>
        )}
        {variant.kind === 'payerGroups' && (
          <StandardModal.RecordName>{`Payer: ${variant.stediPayer.name}`}</StandardModal.RecordName>
        )}

        {step === 'pick' && (
          <StediPayerTypeahead
            value={offeredPayers}
            onChange={setPickedPayers}
            groupId={variant.kind === 'groupPayers' ? variant.group.id : undefined}
          />
        )}

        {step === 'statuses' && variant.kind === 'payers' && (
          <>
            <BodySmall>
              {`Select which facilities to add ${selectedOf(payerCount, 'payer', 'payers')} to and then indicate their network status. The statuses selected below will be applied for ${allOf(payerCount, 'payer', 'payers')}. This can be edited after submission.`}
            </BodySmall>
            {facilityPicker}
            <StatusFields
              intro={`Set network status for ${allOf(payerCount, 'payer', 'payers')}:`}
              value={statuses}
              onChange={setStatuses}
            />
          </>
        )}

        {step === 'statuses' && variant.kind === 'groupPayers' && (
          <>
            <BodySmall>
              {`Indicate the network status of ${selectedOf(payerCount, 'payer', 'payers')} at this facility. The statuses selected below will be applied for ${allOf(payerCount, 'payer', 'payers')}. This can be edited after submission.`}
            </BodySmall>
            <StatusFields
              intro={`Set network status for ${allOf(payerCount, 'payer', 'payers')}:`}
              value={statuses}
              onChange={setStatuses}
            />
          </>
        )}

        {variant.kind === 'payerGroups' && (
          <>
            <BodySmall>
              Select which facilities to add for this payer and then indicate their network status. The statuses
              selected below will be applied for all selected facilities. This can be edited after submission.
            </BodySmall>
            {facilityPicker}
            <StatusFields
              intro={`Set network status for ${allOf(groupIds.length, 'facility', 'facilities')}:`}
              value={statuses}
              onChange={setStatuses}
            />
          </>
        )}
      </Body>
      <StandardModal.Actions>
        {step === 'pick' ? (
          <>
            <Button variant='ghost' onClick={onClose}>
              Cancel
            </Button>
            <Button variant='primary' disabled={payerCount === 0} onClick={() => setStep('statuses')}>
              Next
            </Button>
          </>
        ) : (
          <>
            <Button
              variant='ghost'
              onClick={() => (variant.kind === 'payerGroups' ? onClose() : setStep('pick'))}
              disabled={isPending}>
              Go Back
            </Button>
            <Button
              variant='primary'
              disabled={stediPayerIds.length === 0 || sentGroupIds.length === 0 || isPending}
              loading={isPending}
              onClick={() => mutate({ stediPayerIds, groupIds: sentGroupIds, ...statuses })}>
              Submit
            </Button>
          </>
        )}
      </StandardModal.Actions>
    </StandardModal>
  );
}

// Which of the picked payers are already added, where this variant adds them.
function addedLookup(variant: AddVariant, ids: string[]) {
  if (variant.kind === 'groupPayers') {
    const params = { groupId: variant.group.id, stediPayerIds: ids, page: 1, pageSize: ids.length };
    return queryOptions({
      queryKey: groupPayerNetworkStatusQueryKeys.rows(params),
      queryFn: ({ signal }) =>
        indexGroupPayerNetworkStatuses(params, signal).then((data) => data.data.map((row) => row.stediPayerId)),
    });
  }
  const params = { stediPayerIds: ids, page: 1, pageSize: ids.length };
  return queryOptions({
    queryKey: groupPayerNetworkStatusQueryKeys.payers(params),
    queryFn: ({ signal }) => indexPayerNetworkSummaries(params, signal).then((data) => data.data.map(({ id }) => id)),
  });
}

function successText(variant: AddVariant, payerCount: number, groupCount: number) {
  const payers = countOf(payerCount, 'payer', 'payers');
  const facilities = countOf(groupCount, 'facility', 'facilities');
  if (variant.kind === 'payerGroups') return `${facilities} added to ${variant.stediPayer.name}`;
  if (variant.kind === 'groupPayers') return `${payers} added at ${variant.group.name}`;
  return `${payers} added at ${facilities}`;
}

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 32px;
`;
